// Tracker — a cola fina: resolve session_id/anonymous_id (localStorage), constrói o evento pelo
// contrato e o entrega à EventQueue. A política de envio D.5 mora na fila; o transporte, nos
// adaptadores. Agnóstico (REGRA #1).
import { captureAttribution, enrichWithAttribution } from "./attribution.js";
import { contextoCompacto, pediuParaNaoRastrear } from "./contexto.js";
import { marcarVida, primeiraVisita, sessaoDeAgora } from "./sessao.js";
import { buildEvent, DEFAULT_SIGNAL_EVENTS } from "./contract.js";
import { aplicarGuardado, renovarGuardado, CHAVE_DECISAO } from "./decisao.js";
import { pseudonymizeMsisdn } from "./identity.js";
import { browserDom, paginaAtual, PersonalizationClient } from "./personalization.js";
import { EventQueue } from "./queue.js";
import { comoHtml } from "./conteudo.js";
import { aplicarSlots } from "./resolve.js";
import { browserStorage, browserTransport, browserValueStorage, registerExitHooks } from "./transport.js";

/** O nome do evento de impressão. Underscore, como o contrato manda (REGRA #8). */
export const EVENTO_IMPRESSAO = "personalization_shown";

/** O clique no que foi personalizado. Fecha o funil: impressão -> clique. */
export const EVENTO_CLIQUE = "personalization_clicked";

const SESSION_KEY = "synqi3_session_id";
const ANON_KEY = "synqi3_anon_id";

/**
 * O EIXO DO BRAÇO que a POLÍTICA declarou, pronto para entrar nos props. `{}` quando não há.
 *
 * O braço do bandit é `{contexto}:{variante}`, e quem escolhe o contexto é a política do tenant
 * (`bandit.context`). O `bandit_update` lê esse eixo dos props da impressão, pelo NOME dele. Este
 * eco levava uma lista FIXA — `slot, variant, rule, segment` —, então qualquer eixo que não fosse
 * `segment` chegava ausente ao lago, caía no `COALESCE(..., '_all')` e TODOS os braços colapsavam
 * num contexto só. Sem erro e sem aviso, porque `_all` é um valor legítimo: o bandit virava
 * não-contextual e seguia parecendo que aprendia por contexto.
 *
 * Levar o que a plataforma DECLAROU, em vez de uma segunda lista aqui, é o que impede os dois lados
 * de divergirem no próximo eixo que alguém inventar.
 *
 * Escalar só, e o valor é rejeitado se for objeto: props de evento não carregam estrutura, e um
 * `[object Object]` no caminho do braço seria pior que a ausência.
 */
function eixoDoBraco(a) {
  const c = a && a.context;
  if (!c || typeof c.key !== "string" || !c.key) return {};
  const v = c.value;
  if (v === null || v === undefined || typeof v === "object" || typeof v === "function") return {};
  return { [c.key]: v };
}

function uuid() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
}

function getOrCreateLocalId(key) {
  try {
    const existing = localStorage.getItem(key);
    if (existing) return existing;
    const id = uuid();
    localStorage.setItem(key, id);
    return id;
  } catch {
    return uuid();
  }
}

/** O storage da sessão, ou um dublê inerte fora do navegador. `sessao.js` trata a falha dos dois. */
function _storageDeSessao() {
  try {
    if (typeof localStorage !== "undefined") return localStorage;
  } catch { /* bloqueado: cai no inerte */ }
  return { getItem: () => null, setItem: () => {} };
}


function browserAttribution() {
  try {
    if (typeof location === "undefined" || typeof localStorage === "undefined") return {};
    return captureAttribution({ params: new URLSearchParams(location.search), storage: localStorage });
  } catch {
    return {};
  }
}

export class Tracker {
  constructor(opts) {
    this.tenantId = opts.tenantId;
    this.userId = opts.userId || null;
    // A SESSÃO TEM FIM. Era `getOrCreateLocalId`, que guardava o id em `localStorage` e nunca o
    // trocava: a "sessão" de um visitante durava meses, e o KPI `sessions` (`distinct_sessions`)
    // contava PESSOAS com nome de sessão. Ver `sessao.js` para a medição e para o que isto muda nos
    // números de quem já usa o SDK.
    const _ses = opts.sessionId
      ? { id: opts.sessionId, nova: false, motivo: undefined }
      : sessaoDeAgora({ storage: _storageDeSessao(), novoId: uuid });
    this.sessionId = _ses.id;
    /** `true` quando ESTA carga abriu uma sessão. Lido por `captura.js` para o `session_start`. */
    this.sessaoNova = _ses.nova;
    this.sessaoMotivo = _ses.motivo;
    /** A PRIMEIRA visita de todas, em ISO. `undefined` sem storage — ver `sessao.js`. */
    this.primeiraVisita = primeiraVisita({ storage: _storageDeSessao() });
    /** O contexto completo da visita, preenchido por `ligarCaptura`. `null` fora do navegador. */
    this.contextoDaVisita = null;

    // GLOBAL PRIVACY CONTROL, NO PONTO ÚNICO POR ONDE TODO EVENTO PASSA.
    //
    // Até 02/10/2026 a checagem vivia só no `ligarCaptura`, e nem lá cobria tudo: `paginaVista()`
    // ficava fora do gate e o `_ouvirScroll` deste módulo não sabia de GPC nenhum. Medido num
    // navegador real, com `navigator.globalPrivacyControl = true` e a tag instalada: saíam
    // `page_view` ×2 e `scroll_depth` ×5. Não saíam `session_start`, `click` nem `page_leave` — os
    // três que o gate alcançava.
    //
    // Isso quebrava uma promessa ESCRITA. O aviso de privacidade de um inquilino publicou, com o
    // texto que eu forneci: *"Se o seu navegador envia o sinal Global Privacy Control, o SYNQi³ não
    // mede nada."* Gate em dois dos cinco caminhos não é isso.
    //
    // Por que no `track` e não em cada emissor: `track` é o funil de TODOS eles — `ligarCaptura`, o
    // `_ouvirScroll`, o `_marcarTempoNaPagina`, o eco de impressão da personalização, e o código do
    // próprio site. Gate por emissor é convenção, e o sexto emissor nasce sem ele.
    //
    // `data-synqi3-ignore-gpc` continua existindo, e continua sendo a decisão do DONO da página: o
    // risco de ignorar o sinal é dele, e por isso vai escrito na tag dele.
    this.respeitarGpc = opts.ignorarGpc !== true;
    // UM PORTÃO, COM MOTIVO — e não um booleano por razão de silêncio. O motivo volta para quem
    // chama `consent`, então os VALORES dele são contrato, e por isso estão em inglês. Em 02/10/2026
    // chegou o segundo motivo (consentimento revogado), e dois booleanos gatilhando o mesmo `track`
    // é o começo de "o terceiro nasce sem ser consultado": quem acrescentar um motivo acrescenta um
    // `if`, e o `if` esquecido não dá erro, dá evento saindo.
    //
    //     null        pode emitir
    //     "gpc"       o navegador pediu para não rastrear
    //     "consent"   a pessoa revogou, ou nunca consentiu
    // O `nav` fica GUARDADO porque o GPC é reconsultado quando o site consente: sem ele,
    // `consent(true)` leria o `navigator` real num teste que injetou um falso, e a trava do
    // GPC passaria a medir outro navegador que não o do caso.
    this._nav = opts.nav || (typeof navigator !== "undefined" ? navigator : null);
    this.silenced = this.respeitarGpc && pediuParaNaoRastrear({ nav: this._nav }) ? "gpc" : null;
    this.anonymousId = opts.anonymousId || getOrCreateLocalId(ANON_KEY);
    this.signalEvents = opts.signalEvents || DEFAULT_SIGNAL_EVENTS;
    this.attribution = opts.attribution !== undefined ? opts.attribution : browserAttribution();  // fbclid/gclid/utm
    this.msisdnSalt = opts.msisdnSalt || "";        // salt por-tenant da pseudonimização (LGPD)
    this.countryCode = opts.countryCode || "";

    const transport = opts.transport || browserTransport({
      baseUrl: opts.baseUrl, tenantId: this.tenantId, sdkToken: opts.sdkToken, publicKey: opts.publicKey,
    });
    const storage = opts.storage !== undefined
      ? opts.storage
      : (typeof localStorage !== "undefined" ? browserStorage() : null);

    // Superfície onde a personalização é aplicada, e onde a decisão guardada mora. Injetáveis pelo
    // mesmo motivo que o transporte: o SDK roda fora de navegador, e teste não abre janela.
    this.dom = opts.dom || browserDom();
    this.baseUrl = opts.baseUrl;
    // UMA credencial, um nome. `sdkToken` e `publicKey` são o MESMO valor de `X-SYNQ-TOKEN`: o
    // primeiro vence 1 h e nasce de uma sessão autenticada, o segundo é estático e vive no bundle
    // de um site anônimo. Quem resolve os dois é o authorizer, pelo mesmo caminho.
    //
    // Até 29/09/2026 só o `sdkToken` chegava aqui, e a personalização ficava calada num site de
    // chave pública: o gate da linha do `renovarGuardado` via `undefined` e devolvia sem perguntar,
    // e o WebSocket subia sem credencial. O escopo que a chave pública recebe
    // (`escopo.do_navegador()`) inclui `personalization:read` — então o produto prometia um alcance
    // que o SDK não entregava.
    this.credencial = opts.sdkToken || opts.publicKey;
    // `fetch` injetável, pela mesma razão do `resolve.js`: trocar de runtime é trocar o adaptador,
    // não o código — e teste não deve depender de mexer no global.
    this.fetch = opts.fetch;
    this.decisaoStorage = opts.decisaoStorage !== undefined
      ? opts.decisaoStorage
      : (typeof localStorage !== "undefined" ? browserValueStorage(CHAVE_DECISAO) : null);

    this.queue = new EventQueue({ transport, storage, signalEvents: this.signalEvents, flush: opts.flush });
    if (opts.registerExit !== false) registerExitHooks(this.queue);
    void this.queue.replay();                          // D.5.4: reenvia o pendente da sessão anterior

    // Personalização em tempo real (opcional): só se wsUrl for dado.
    this.personalization = null;
    if (opts.wsUrl) {
      this.personalization = new PersonalizationClient({
        wsUrl: opts.wsUrl, tenantId: this.tenantId, sessionId: this.sessionId, sdkToken: this.credencial,
        getUserId: () => this._uid(),
        socketFactory: opts.socketFactory, dom: this.dom, autoApply: opts.autoApplyPersonalization,
        onPersonalization: opts.onPersonalization, onMessage: opts.onMessage,
      });
      if (opts.connectWs !== false) this.personalization.connect();
    }

    // ── ENGAJAMENTO: profundidade de rolagem e tempo na página ──────────────────────────────
    //
    // Os dois EXISTEM em produção e não existiam aqui. Medido em 03/09/2026: o catálogo de eventos
    // do `valfredo` tem CINCO tipos, e dois deles são `scroll_depth` e `time_on_page` — 40% do
    // vocabulário do tenant. O `fabrik7` também os emite. Quem os produz é uma CÓPIA do SDK de
    // maio, vendorizada dentro dos projetos; este arquivo, o canônico, nunca teve nenhum dos dois.
    //
    // Isso invertia a migração: trocar a cópia pelo bundle publicado — que era a recomendação
    // óbvia, e a que eu tinha dado — SILENCIARIA dois dos cinco sinais do maior case. Portar para
    // cá é o que torna a migração uma troca sem perda.
    //
    // Ligados por padrão, como na cópia (`opts.autoScroll !== false`), porque o objetivo é que
    // migrar não mude o que o tenant recebe. `scroll: false` / `tempoNaPagina: false` desligam.
    this._maiorMarco = 0;
    this.marcosDeScroll = opts.marcosDeScroll || [25, 50, 75, 90, 100];
    this._desdeMs = opts.agora ? opts.agora() : Date.now();
    this.agora = opts.agora || (() => Date.now());
    // `timer` injetável pela mesma razão do `fetch` e do `dom`: teste não deve depender de relógio
    // real, e outro runtime troca o adaptador em vez do código.
    //
    // SEM NAVEGADOR, NÃO HÁ TIQUE. `setInterval` existe no Node, e um intervalo sem `unref` segura
    // o event loop para sempre: a primeira versão disto pendurou a suíte do próprio SDK — que foi
    // como o defeito apareceu. Tempo na página é um fato do NAVEGADOR; num renderizador de
    // servidor ele não existe, e ligá-lo ali seria inventar sinal.
    // ENVOLVIDO NUMA SETA, e não `setInterval` solto. Esta linha guardava a referência nua, e
    // `this.timer(...)` a chamava com `this` sendo o Tracker — o que o Chrome recusa com
    // `TypeError: Illegal invocation`. Medido num navegador de verdade em 02/10/2026, em produção:
    //
    //     TypeError: Illegal invocation
    //       at E._marcarTempoNaPagina (synqi3.min.js:1:25111)
    //       at new E (synqi3.min.js:1:24549)
    //
    // O efeito era devastador e silencioso. O construtor levantava DEPOIS de `_ouvirScroll()`, então
    // `scroll_depth` saía normalmente — e `iniciar` devolvia `null`, de modo que `window.synqi3` nunca
    // existia, `ligarCaptura` nunca rodava (zero `session_start`, `page_view`, `page_leave`, `click`,
    // `form_*`) e a personalização nunca acontecia. Conferido no lake do primeiro inquilino que
    // instalou: 12 eventos, TODOS `scroll_depth`, nenhum outro tipo.
    //
    // E o meu "provado no ar" não pegou: o harness roda em Node, onde `setInterval` não liga para o
    // `this`. Dublê sem o tipo da produção não prova nada — e o `queue.js` já fazia certo
    // (`(fn, ms) => setTimeout(fn, ms)`) duas linhas de distância.
    //
    // Chrome recusa solto: setInterval, setTimeout, clearInterval, clearTimeout,
    // requestAnimationFrame, queueMicrotask. Aceita: fetch. Medido, não suposto.
    this.timer = opts.timer
      || (typeof window !== "undefined" && typeof setInterval !== "undefined"
          ? ((fn, ms) => setInterval(fn, ms))
          : null);
    this.intervaloDoTempoMs = opts.intervaloDoTempoMs || 60000;   // 60s: a cópia baixou de 10s por ruído
    // O TETO, em segundos À VISTA. Medido em 03/11/2026 no lake: em outubro, 43 mil dos ~225 mil
    // eventos da plataforma eram `time_on_page` de TRÊS abas esquecidas abertas — uma delas chegou a
    // `seconds: 1350424`, quinze dias, com um evento por minuto. O plano cobra por evento, e uma aba
    // esquecida consumia 43% do teto do degrau de entrada sem dizer nada sobre engajamento. Meia hora
    // à vista é o limite a partir do qual mais um minuto não muda a leitura (é também a janela de
    // sessão de mercado); depois dela o tique para.
    this.tempoMaximoS = opts.tempoMaximoS || 1800;
    this._visivelMs = 0;
    this._ultimoTique = this._desdeMs;
    if (opts.scroll !== false) this._ouvirScroll();
    if (opts.tempoNaPagina !== false) this._marcarTempoNaPagina();
  }

  /**
   * `scroll_depth` uma vez por marco atingido, e nunca duas.
   *
   * O marco é o MAIOR já alcançado, não o atual: rolar de volta para cima e descer de novo não
   * pode reemitir. Contagem em dobro é pior que zero — zero denuncia, dobro parece medida (é a
   * mesma razão do ouvinte único em `_ouvirCliques`).
   *
   * TODOS os marcos cruzados, e não só o mais alto. Até 02/10/2026 este laço fazia `.pop()` no maior
   * e descartava os de baixo: quem pulasse de 0% a 100% numa rolagem só gerava `percent: 100` e
   * NENHUM `percent: 25`. Um funil de profundidade ("quantos passaram de 25%?") subcontava
   * silenciosamente — e quem chegou a 100% necessariamente passou por 25%. O `ligarCaptura` já emitia
   * todos; manter dois critérios para a mesma pergunta daria duas respostas conforme o caminho.
   */
  _ouvirScroll() {
    if (!this.dom || !this.dom.onScroll || !this.dom.profundidadeVisivel) return;
    this._pararScroll = this.dom.onScroll(() => {
      const pct = this.dom.profundidadeVisivel();
      const novos = this.marcosDeScroll
        .filter((m) => m > this._maiorMarco && pct >= m)
        .sort((a, b) => a - b);
      if (!novos.length) return;
      this._maiorMarco = novos[novos.length - 1];
      for (const m of novos) this.track("scroll_depth", { percent: m });
    });
  }

  /**
   * Desliga a rolagem PRÓPRIA do tracker, porque alguém mais completo assumiu a medição.
   *
   * MECANISMO, e não convenção. Medido em 02/10/2026 com o adaptador real do navegador: um único
   * evento de rolagem produzia **5** `scroll_depth` — quatro do `ligarCaptura` e um deste ouvinte.
   * Eu havia construído um segundo mecanismo ao lado do que já existia, e dobro não denuncia como
   * zero denuncia: parece medida.
   *
   * A saída óbvia seria `browser.js` passar `scroll: false` ao construir. Isso é convenção: quem
   * montasse o Tracker na mão e chamasse `ligarCaptura` teria a duplicata de volta, calado. Aqui o
   * próprio `ligarCaptura` desliga, então vale para qualquer caminho de montagem.
   */
  pararRolagemPropria() {
    if (typeof this._pararScroll === "function") {
      try { this._pararScroll(); } catch { /* o ouvinte já pode ter ido */ }
    }
    this._pararScroll = null;
    this.rolagemPropriaDesligada = true;
  }

  /**
   * `time_on_page` a cada intervalo, com os segundos À VISTA acumulados desde o início da página.
   *
   * À VISTA, e não de relógio: aba escondida não emite e não acumula, porque ninguém está na página.
   * O intervalo que passou escondido é descartado inteiro. Ao chegar a `tempoMaximoS`, emite o
   * último valor e o tique para. Ver o teto no construtor.
   */
  _marcarTempoNaPagina() {
    if (!this.timer) return;
    this._pararTempo = this.timer(() => {
      const agora = this.agora();
      const passou = agora - this._ultimoTique;
      this._ultimoTique = agora;
      if (this.dom && typeof this.dom.visivel === "function" && !this.dom.visivel()) return;
      this._visivelMs += passou;
      const seconds = Math.min(Math.round(this._visivelMs / 1000), this.tempoMaximoS);
      this.track("time_on_page", { seconds });
      if (seconds >= this.tempoMaximoS) this._pararTempoNaPagina();
    }, this.intervaloDoTempoMs);
    // `unref` quando o runtime oferece: um tique pendente não deve impedir o processo de terminar.
    if (this._pararTempo && typeof this._pararTempo.unref === "function") this._pararTempo.unref();
  }

  /** Pede personalização (WS). Descobre os slots [data-synqi3-slot] no DOM se não vierem. */
  refreshPersonalization(slots) {
    if (this.personalization) this.personalization.requestPersonalization(slots);
  }

  // ── PERSONALIZAR: o ciclo inteiro, numa chamada ────────────────────────────────────────────
  //
  //     const t = new Tracker({ baseUrl, tenantId, sdkToken, userId });
  //     t.personalizar();
  //
  // É o método que fecha o laço sente -> prevê -> decide -> AGE, do lado do navegador. Três coisas
  // acontecem, e a ORDEM é o que faz a coisa funcionar:
  //
  //   1. aplica a decisão GUARDADA, síncrono e sem rede, antes de a página pintar (~0 ms medido)
  //   2. ECOA a impressão do que foi aplicado — sem isto a plataforma nunca sabe o que mostrou
  //   3. pergunta à plataforma DEPOIS de pintar, e guarda para a próxima visita
  //
  // Inverter 1 e 3 recria o flicker que este caminho existe para matar: medido em 21/08/2026, a
  // ida até a região custa ~590 ms e a janela até a pintura é de 86 ms.
  //
  // ## Por que o passo 2 não é detalhe
  //
  // Medido em 22/08/2026: `personalization_shown` tinha TRÊS consumidores no produto (o freq cap
  // do `/decide`, o `bandit_update` e o KPI `offer_impressions`) e ZERO produtores no caminho de
  // navegador. No lago inteiro, todos os tenants, toda a história: nenhum evento. Consequência
  // medida: 14.264 perfis no decision-cache, nenhum com contador `imp_`. O teto de frequência
  // nunca capou nada em tenant nenhum, e o bandit nunca teve prior real para aprender.
  //
  // Uma capacidade afirmada em três lugares e produzida em nenhum é a mesma quebra de premissa
  // que este produto persegue no dado, um nível acima.

  /**
   * Aplica a personalização desta página e ecoa o que mostrou.
   *
   * @param {object} [o]
   * @param {string[]} [o.slots]           quais lugares; sem isso, descobre no DOM
   * @param {boolean} [o.aplicarAoRenovar] aplicar também o que a rede trouxer (default `true`)
   * @returns {Promise<{aplicados: Array, respondeu: boolean, doGuardado: number, daRede: number}>}
   */
  async personalizar({ slots, aplicarAoRenovar = true } = {}) {
    const nomes = (slots && slots.length ? slots : this.dom.collectSlots()) || [];
    const vazio = { aplicados: [], respondeu: false, doGuardado: 0, daRede: 0 };
    if (!nomes.length) return vazio;

    // 1) ANTES DE PINTAR — o guardado, sem rede.
    const doGuardado = this.decisaoStorage
      ? aplicarGuardado({
          storage: this.decisaoStorage, tenant: this.tenantId, userId: this._uid(),
          aplicar: (slot, texto, dado) => this._aplicarNoDom(slot, texto, dado),
        })
      : [];
    this._ecoarImpressoes(doGuardado, true);

    // 2) DEPOIS DE PINTAR — pergunta e guarda para a próxima visita.
    if (!this.baseUrl || !this.credencial || !this.decisaoStorage) {
      return { aplicados: doGuardado, respondeu: false, doGuardado: doGuardado.length, daRede: 0 };
    }
    const r = await renovarGuardado({
      storage: this.decisaoStorage, tenant: this.tenantId, userId: this._uid(),
      baseUrl: this.baseUrl, sdkToken: this.credencial, slots: nomes, fetch: this.fetch,
      // Mesma resposta que o WebSocket manda: um contrato, dois transportes.
      page: paginaAtual() || undefined,
    });

    // 3) O que a rede trouxe entra na página SÓ para quem ainda não tinha decisão. Aplicar por cima
    //    do que já está na tela seria trocar o conteúdo debaixo dos olhos de quem já está lendo — e
    //    o guardado é a decisão vigente até vencer.
    let daRede = [];
    if (aplicarAoRenovar && r.respondeu) {
      const jaAplicados = new Set(doGuardado.map((a) => a.slot));
      const pendentes = Object.fromEntries(
        Object.entries(r.slots || {}).filter(([slot]) => !jaAplicados.has(slot)));
      daRede = aplicarSlots(pendentes, (slot, texto, dado) => this._aplicarNoDom(slot, texto, dado));
      this._ecoarImpressoes(daRede, false);
    }
    const aplicados = [...doGuardado, ...daRede];
    this._ouvirCliques(aplicados);
    return { aplicados, respondeu: r.respondeu,
             doGuardado: doGuardado.length, daRede: daRede.length };
  }

  /**
   * O clique no que foi personalizado, com a MESMA trilha da impressão.
   *
   * `offer_clicks` e a taxa `offer_ctr` (cliques / impressões) existem no catálogo de KPI desde
   * sempre e nunca tiveram produtor — medido em 22/08/2026, junto com a impressão.
   *
   * DUAS RECUSAS, e as duas são o que separa medir de inflar:
   *
   *   1. só conta clique em slot REALMENTE aplicado. Um lugar que mostrou o texto da própria
   *      página não teve personalização, e atribuir o clique a ela superestimaria o efeito.
   *   2. um ouvinte só, trocado a cada chamada. Pendurar mais um por visita faria o mesmo clique
   *      virar dois eventos, e contagem em dobro é pior que zero: zero denuncia, dobro parece
   *      medida.
   */
  _ouvirCliques(aplicados) {
    if (this._pararCliques) { this._pararCliques(); this._pararCliques = null; }
    if (!this.dom || !this.dom.onSlotClick || !(aplicados || []).length) return;
    const porSlot = new Map(aplicados.map((a) => [a.slot, a]));
    this._pararCliques = this.dom.onSlotClick((slot, extra) => {
      const a = porSlot.get(slot);
      if (!a) return;
      // O eixo entra ANTES, e não é ordem por acaso: a trilha (`slot`, `variant`, `rule`) é o que
      // identifica a decisão, e um eixo autorado com um desses nomes não pode sobrescrevê-la. Quem
      // vence é o literal abaixo, por construção — sem uma segunda lista de nomes proibidos.
      this.track(EVENTO_CLIQUE, {
        ...eixoDoBraco(a),
        slot: a.slot, variant: a.variant, rule: a.rule, segment: a.segment,
        cta: (extra && extra.cta) || null,
        decision_ts: a.decision_ts ?? null,
      });
    });
  }

  _aplicarNoDom(slot, texto, dado) {
    if (!this.dom || !this.dom.applySlot) return false;
    // A MARCAÇÃO SAI DO `conteudo.js`, num lugar só. Antes era `c.html || texto`, e por isso o
    // objeto de estilo do formato, a CTA e seis dos nove tipos de conteúdo nunca chegavam à tela:
    // o produto os declarava, a tela de autoria os ofereceria, e ninguém os consumia.
    const html = comoHtml(dado);
    if (html == null) return false;               // sem o que mostrar não substitui nada
    this.dom.applySlot(slot, { html, data: dado });
    return true;
  }

  /**
   * Um evento por slot APLICADO. Nunca por slot decidido, nunca por slot pedido.
   *
   * A impressão é uma OCORRÊNCIA: a mesma pessoa vendo a mesma variante duas vezes são dois fatos,
   * e o `event_id` é sorteado por isso (o `buildEvent` já faz). Derivá-lo de (usuário, slot,
   * variante) faria o motor deduplicar exibições reais e o bandit aprender de uma amostra só.
   *
   * `decision_age_ms` existe porque uma decisão guardada foi tomada ANTES de ser mostrada. Sem ele,
   * o lago não distingue "decidiu agora e mostrou" de "mostrou o que decidira há uma hora" — e o
   * produto diz a idade do que mostra.
   */
  _ecoarImpressoes(aplicados, doGuardado) {
    const agora = Date.now();
    for (const a of aplicados || []) {
      // O eixo entra ANTES da trilha, pelo mesmo motivo do clique: `slot`/`variant`/`rule`
      // identificam a decisão e não podem ser sobrescritos por um eixo autorado com esses nomes.
      this.track(EVENTO_IMPRESSAO, {
        ...eixoDoBraco(a),
        slot: a.slot, variant: a.variant, rule: a.rule, segment: a.segment,
        stored: !!doGuardado,
        decision_ts: a.decision_ts ?? null,
        decision_age_ms: a.decision_ts ? Math.max(0, agora - a.decision_ts) : 0,
      });
    }
  }

  _uid() {
    return this.userId || this.anonymousId;
  }

  /** Identifica o usuário (evento de SINAL → imediato). Sem user_id, sem profile (REGRA #3). */
  identify(userId, props = {}) {
    this.userId = userId;
    if (userId) this.track("identify", { user_id: userId, ...props });
  }

  /**
   * Identifica pelo MSISDN (número do cliente) — LGPD: pseudonimiza na ORIGEM e usa o pseudônimo como
   * user_id. O número em claro NUNCA entra num evento nem é persistido. Assíncrono (Web Crypto).
   * @returns {Promise<string|null>} o pseudônimo (ou null se o número for vazio)
   */
  async identifyByMsisdn(msisdn, props = {}) {
    const pseudo = await pseudonymizeMsisdn(msisdn, { salt: this.msisdnSalt, countryCode: this.countryCode });
    if (pseudo) this.identify(pseudo, props);       // o msisdn cru NÃO vai em props
    return pseudo;
  }

  track(type, props = {}) {
    // O FUNIL. Ver `this.silenced` no construtor: nenhum evento sai, de nenhum caminho, por nenhum
    // dos motivos. `track` é o funil de TODOS os emissores, e é por isso que o portão mora aqui.
    //
    // Devolve `null` e não um evento falso: quem chama pode querer saber que não houve emissão, e
    // devolver o objeto construído faria o chamador acreditar que ele foi enfileirado.
    if (this.silenced) return null;
    const ev = buildEvent({
      type,
      user_id: this.userId || undefined,
      anonymous_id: this.anonymousId,
      session_id: this.sessionId,
      url: typeof location !== "undefined" ? location.href : undefined,
      referrer: typeof document !== "undefined" ? document.referrer || undefined : undefined,
      user_agent: typeof navigator !== "undefined" ? navigator.userAgent : undefined,
      // O contexto COMPACTO vem primeiro e o do chamador VENCE: quem foi explícito não tem o valor
      // trocado por um default. A atribuição (fbclid/gclid/utm) entra por cima, como sempre entrou.
      props: enrichWithAttribution(
        { ...contextoCompacto(this.contextoDaVisita || {}), ...props }, this.attribution),
    });
    this.queue.enqueue(ev);
    // O sinal de vida da sessão é renovado a cada evento: sem isto, a janela de 30 min mediria o tempo
    // desde a ABERTURA e não desde a última atividade, e uma visita longa seria cortada no meio.
    marcarVida({ storage: _storageDeSessao() });
    return ev;
  }

  /**
   * UMA CONVERSÃO, que é o fato que o catálogo de KPI já conta (`conversions`, `agg: count` sobre
   * `event_type: "conversion"`).
   *
   * Existe como método próprio porque o nome do tipo importa: um site que manda `compra_feita` ou
   * `lead` não aparece em nenhum KPI, e o operador vê card vazio sem saber que o nome é o problema.
   * Aqui o tipo é fixo e o que o site declara é o RÓTULO do que converteu.
   *
   * O NOME está em inglês — e foi em português por um dia. `conversao()` ao lado de `track()`,
   * `identify()` e `trackPageView()` punha um quarto dialeto na superfície que o desenvolvedor do
   * inquilino escreve, e a API pública é contrato tanto quanto o fio. Medido em 02/10/2026: zero
   * chamadores no repositório, zero evento `conversion` no lake, nenhum inquilino com a tag
   * instalada — então o rename custou nada e não ganhou apelido de compatibilidade, porque apelido
   * para método de um dia é justamente como o segundo dialeto sobrevive.
   */
  conversion(label, { value, currency, ...props } = {}) {
    const p = { ...props };
    if (label) p.label = label;
    // `value` só entra se for número de verdade: string vazia e `NaN` virariam zero no agregador, e
    // zero é uma afirmação sobre o negócio ("converteu sem valor") que ninguém fez.
    if (typeof value === "number" && Number.isFinite(value)) p.value = value;
    if (currency) p.currency = String(currency).toUpperCase().slice(0, 3);
    return this.track("conversion", p);
  }

  /** A atribuição capturada (fbclid/gclid/utm). Útil p/ debug/integrações. */
  getAttribution() {
    return { ...this.attribution };
  }

  trackPageView(props = {}) {
    return this.track("page_view", {
      path: typeof location !== "undefined" ? location.pathname : undefined,
      title: typeof document !== "undefined" ? document.title : undefined,
      ...props,
    });
  }

  /** Força o flush do lote analítico (ex.: antes de um redirect). */
  async flushNow() {
    await this.queue.flushBuffer();
  }

  // ── CONSENTIMENTO: revogar tem de NEGAR NA HORA, sem recarregar a página ──────────────────────
  //
  // Pedido em 02/10/2026 por quem pôs o SDK dentro de um banner de consentimento: o padrão exige
  // que o "não" valha imediatamente, e o SDK não tinha método para isso. O que existia era
  // `silenciadoPorGpc`, uma propriedade INTERNA, e o site estava ligando-a na mão. Funcionava e
  // media zero chamadas, mas é dependência de um nome que eu podia renomear na semana seguinte,
  // sem erro e sem aviso: a página voltaria a medir, calada, num produto cuja política publicada
  // promete o contrário.
  //
  // Por que `silenciadoPorGpc` continua existindo: ele é ACESSOR agora, derivado do
  // `this.silenced`. Quem já escrevia nele segue funcionando e cai no mecanismo único em vez de
  // criar um segundo estado ao lado. Compatibilidade sem cópia.

  /**
   * Liga ou desliga TODA a medição, na hora.
   *
   * `consent(false)` faz quatro coisas, e as quatro foram pedidas porque as quatro vazam
   * medição se faltarem:
   *
   *   1. fecha o funil (`this.silenced`), então nenhum emissor passa, inclusive os que ainda não
   *      existem
   *   2. desfaz o que está INSTALADO no navegador: ouvintes, os embrulhos de `pushState`/
   *      `replaceState` e o tique de tempo. Sem isto o `track` recusa, mas o trabalho continua
   *      rodando na página de quem nos hospeda
   *   3. esvazia a fila, em memória E no `localStorage`. Sem isto o que já estava enfileirado sai
   *      no próximo flush, ou na próxima visita: revogação que deixa a fila cheia não revogou nada
   *   4. com `{ eraseData: true }`, remove as chaves `synqi3_*`. É opcional porque apagar o
   *      `anonymous_id` perde a continuidade de quem consentir de novo, e essa é decisão do site
   *
   * `consent(true)` reabre o funil e, quando houver como, religa a captura. Devolve o motivo
   * do silêncio (`null` quando pode emitir), que é o que permite testar em vez de confiar.
   *
   * O NOME É EM INGLÊS, como `track` e `identify`: o método que o desenvolvedor do inquilino
   * escreve é contrato com o cliente, igual à chave e ao VALOR que viajam no fio. Eu tinha posto
   * quatro nomes em português nesta superfície horas depois de tirar `conversao()` dela pelo
   * mesmo motivo.
   */
  consent(ok, { eraseData = false } = {}) {
    if (ok === false) {
      this.silenced = "consent";
      this._desmontarCaptura();
      this.clearQueue({ eraseData });
      return this.silenced;
    }
    // GPC VENCE O "SIM". O sinal do navegador não é uma preferência que o banner do site revoga: se
    // ele está ligado, consentir no banner não reabre a medição. O contrário faria o site poder
    // pedir um "aceitar" e desfazer o pedido do navegador com ele.
    if (this.respeitarGpc && pediuParaNaoRastrear({
      nav: this._nav || (typeof navigator !== "undefined" ? navigator : null),
    })) {
      this.silenced = "gpc";
      return this.silenced;
    }
    this.silenced = null;
    if (typeof this._religarCaptura === "function") {
      try { this.captura = this._religarCaptura(); } catch { /* a medição não volta; a página vive */ }
    }
    return this.silenced;
  }

  /**
   * `consent(false)`. UMA linha, sem lógica própria: é o nome que um autor de site procura primeiro,
   * e alias que delega não é segundo mecanismo. Se um dia ele ganhar comportamento, virou.
   */
  stop(opts) {
    return this.consent(false, opts);
  }

  /**
   * Esvazia a fila agora, em memória e no armazenamento, sem enviar.
   *
   * Separado do `consent` porque é testável sozinho e porque "revoguei e a fila reapareceu
   * cheia" é a falha específica que o site precisava poder conferir.
   */
  clearQueue({ eraseData = false } = {}) {
    try { this.queue._clearTimer(); } catch { /* sem tique: segue */ }
    try { this.queue.buffer.length = 0; } catch { /* sem buffer: segue */ }
    try { this.queue.storage.write([]); } catch { /* storage bloqueado: segue */ }
    if (eraseData) this.clearStoredData();
  }

  /**
   * Esquece o visitante: apaga as chaves `synqi3_*` E a identidade que mora NA INSTÂNCIA.
   *
   * ## As duas metades, e a segunda faltava
   *
   * Varre o armazenamento por PREFIXO em vez de carregar uma lista de nomes: o SDK grava 15 chaves
   * hoje, e lista repetida aqui ficaria velha na primeira chave nova — que é exatamente o jeito de
   * sobrar dado de quem pediu para apagar.
   *
   * E ZERA A MEMÓRIA, que é a metade que faltava. Medido por quem integrou, em 02/10/2026, no mesmo
   * dia em que esta API nasceu: depois de `consent(false, { eraseData: true })` as chaves somem do
   * `localStorage`, mas um `consent(true)` na MESMA página voltava a emitir com o `anonymous_id`
   * idêntico e o `first_visit` idêntico, porque eles estavam em campos da instância. O aviso de
   * privacidade daquele site diz que revogar *"apaga os identificadores do SYNQi³"*, e a
   * continuidade sobrevivia ao apagamento.
   *
   * E tem pior do que o anônimo, que o relato não alcançou: `this.userId`. Se o site chamou
   * `identify(email)` antes, o e-mail continuava no campo e voltava em TODO evento depois do
   * reaceite. O identificador mais sensível era o único que ninguém tinha olhado.
   *
   * ## Por que não escrever os novos ids agora
   *
   * O id novo fica só em MEMÓRIA. Escrevê-lo recriaria `synqi3_anon_id` no disco um instante depois
   * de apagá-lo, e quem revogou não pode deixar rastro — a próxima carga da página cria o dele
   * normalmente. Uma pessoa revogada não tem nada gravado.
   */
  clearStoredData() {
    // AS LOJAS GLOBAIS, as MESMAS onde `getOrCreateLocalId` e `sessao.js` escrevem. Eu tinha posto
    // uma opção `lojas` para o teste injetar, e ela criou a divergência exata que este método
    // existe para não ter: a varredura olhava a loja injetada enquanto a identidade era gravada na
    // global, e a asserção "nada ficou no disco" dava verde nos DOIS estados. Quem denunciou foi
    // uma sonda de mutação que NÃO derrubou nada. O teste instala um `localStorage` falso no
    // global, que é o caminho da produção, e a opção foi embora: campo injetável que faz o teste
    // medir outro produto é pior que não ter.
    for (const loja of [typeof localStorage !== "undefined" ? localStorage : null,
                        typeof sessionStorage !== "undefined" ? sessionStorage : null]) {
      if (!loja) continue;
      try {
        const alvo = [];
        for (let i = 0; i < loja.length; i += 1) {
          const k = loja.key(i);
          if (k && k.indexOf("synqi3_") === 0) alvo.push(k);
        }
        for (const k of alvo) loja.removeItem(k);
      } catch { /* armazenamento bloqueado: nada a apagar por aqui */ }
    }
    // A MEMÓRIA DA INSTÂNCIA. Tudo o que identifica, inclusive o que o `identify` pôs: deixar
    // `userId` para trás faria o e-mail da pessoa voltar em todo evento depois do reaceite.
    this.userId = null;
    this.anonymousId = uuid();
    this.sessionId = uuid();
    this.sessaoNova = true;
    this.sessaoMotivo = "first";
    this.primeiraVisita = undefined;
    this.attribution = {};          // fbclid/gclid/utm são identificadores de campanha da pessoa
    this.contextoDaVisita = null;
  }

  /** Desfaz tudo o que a medição INSTALOU no navegador. Idempotente. */
  _desmontarCaptura() {
    if (this.captura && typeof this.captura.parar === "function") {
      try { this.captura.parar(); } catch { /* já pode ter ido */ }
    }
    this.pararRolagemPropria();
    this._pararTempoNaPagina();
  }

  /** Para o tique do tempo na página. Idempotente: o teto e a desmontagem chamam o mesmo. */
  _pararTempoNaPagina() {
    if (typeof this._pararTempo === "function") {
      try { this._pararTempo(); } catch { /* nada */ }
      this._pararTempo = null;
    } else if (this._pararTempo && typeof clearInterval !== "undefined") {
      // `setInterval` devolve id, não função. Chrome recusa `clearInterval` desacoplado do `window`,
      // então a chamada vai pelo objeto global: é a mesma cicatriz do `Illegal invocation` do tique.
      try { (typeof window !== "undefined" ? window : globalThis).clearInterval(this._pararTempo); }
      catch { /* nada */ }
      this._pararTempo = null;
    }
  }

  /**
   * Derivado de `this.silenced`: um estado, duas vistas.
   *
   * Nome em português e já PUBLICADO no byte que está no CDN, então ele fica: renomeá-lo quebraria,
   * em silêncio, o site que o está usando como atalho de revogação, e silêncio é o pior modo de
   * falha deste campo. Código novo usa `consent`.
   */
  get silenciadoPorGpc() {
    return this.silenced === "gpc";
  }

  set silenciadoPorGpc(v) {
    this.silenced = v ? "gpc" : null;
  }
}
